import { useState } from "react";
import {
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "ra-core";
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Flame,
  Loader2,
  MessageSquare,
  Send,
  Sparkles,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact } from "../types";

export const OUTREACH_STAGES = [
  {
    value: "new",
    label: "New Lead",
    className:
      "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20",
  },
  {
    value: "hook_ready",
    label: "🎯 Hook Ready",
    className:
      "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border-indigo-500/25",
  },
  {
    value: "dm_sent",
    label: "📤 DM Sent",
    className:
      "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/25",
  },
  {
    value: "replied",
    label: "💬 Replied",
    className:
      "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/25",
  },
  {
    value: "meeting_booked",
    label: "📅 Meeting Booked",
    className:
      "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25",
  },
  {
    value: "nurture",
    label: "🌱 Nurture",
    className:
      "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/25",
  },
] as const;

export const LeadScoreBadge = ({
  score,
  contact,
}: {
  score?: number | null;
  contact?: Contact;
}) => {
  const record = useRecordContext<Contact>();
  const targetScore = score ?? contact?.lead_score ?? record?.lead_score;

  if (targetScore == null || targetScore <= 0) {
    return null;
  }

  const isHot = targetScore >= 90;
  const isWarm = targetScore >= 75 && targetScore < 90;

  return (
    <Badge
      variant="outline"
      className={`inline-flex items-center gap-1 font-semibold tabular-nums ${
        isHot
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
          : isWarm
            ? "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300"
            : "border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-300"
      }`}
      title="AI ICP & Buying Signal Score (0–100)"
    >
      {isHot ? <Flame className="size-3 text-emerald-500" /> : <Zap className="size-3" />}
      <span>{targetScore}</span>
    </Badge>
  );
};

export const OutreachStageSelect = ({
  contact,
}: {
  contact?: Contact;
}) => {
  const record = useRecordContext<Contact>();
  const target = contact ?? record;
  const [update] = useUpdate<Contact>();
  const notify = useNotify();

  if (!target) return null;

  const currentStage = target.outreach_stage || "new";
  const stageMeta =
    OUTREACH_STAGES.find((s) => s.value === currentStage) ?? OUTREACH_STAGES[0];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const nextStage = e.target.value;
    update(
      "contacts",
      {
        id: target.id,
        data: { outreach_stage: nextStage },
        previousData: target,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () => {
          notify(`Outreach stage updated to ${nextStage.replace("_", " ")}`, {
            type: "info",
          });
        },
      },
    );
  };

  return (
    <select
      value={currentStage}
      onClick={(e) => e.stopPropagation()}
      onChange={handleChange}
      aria-label="Outreach stage"
      className={`h-6 rounded-full border px-2.5 text-xs font-medium cursor-pointer focus:outline-none ${stageMeta.className}`}
    >
      {OUTREACH_STAGES.map((stage) => (
        <option
          key={stage.value}
          value={stage.value}
          className="bg-background text-foreground"
        >
          {stage.label}
        </option>
      ))}
    </select>
  );
};

export const JobChangeBadge = ({
  contact,
  className = "",
}: {
  contact?: Contact;
  className?: string;
}) => {
  const record = useRecordContext<Contact>();
  const target = contact ?? record;

  if (!target?.job_change_flagged) {
    return null;
  }

  return (
    <Badge
      variant="destructive"
      className={`inline-flex items-center gap-1 font-medium ${className}`}
      title={
        target.detected_company
          ? `Detected current company on LinkedIn: ${target.detected_company} (CRM company: ${target.company_name || "previous"})`
          : "LinkedIn company differs from expected company"
      }
    >
      <AlertTriangle className="size-3" />
      <span>
        Job Change{target.detected_company ? ` → ${target.detected_company}` : " Flagged"}
      </span>
    </Badge>
  );
};

export const CopyHookButton = ({
  icebreaker,
  size = "sm",
  variant = "outline",
  label = "Copy Hook",
}: {
  icebreaker: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?: "default" | "outline" | "secondary" | "ghost";
  label?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const notify = useNotify();

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(icebreaker);
      setCopied(true);
      notify("AI Icebreaker hook copied to clipboard", { type: "info" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      notify("Failed to copy hook to clipboard", { type: "error" });
    }
  };

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      onClick={handleCopy}
      className="cursor-pointer gap-1.5 shrink-0"
    >
      {copied ? (
        <>
          <Check className="size-3.5 text-emerald-500" />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <Copy className="size-3.5" />
          <span>{label}</span>
        </>
      )}
    </Button>
  );
};

export const AiIcebreakerCard = ({ contact }: { contact?: Contact }) => {
  const record = useRecordContext<Contact>();
  const target = contact ?? record;
  const [selectedTab, setSelectedTab] = useState<number>(-1);
  const [update] = useUpdate<Contact>();
  const notify = useNotify();

  if (!target?.ai_icebreaker && !target?.ai_dm_variants?.length) {
    return null;
  }

  const variants = Array.isArray(target.ai_dm_variants)
    ? target.ai_dm_variants
    : [];

  const activeMessage =
    selectedTab === -1
      ? target.ai_icebreaker || variants[0]?.message || ""
      : variants[selectedTab]?.message || target.ai_icebreaker || "";

  const handleCopyAndOpenLinkedIn = async () => {
    if (activeMessage) {
      await navigator.clipboard.writeText(activeMessage).catch(() => {});
    }
    if (target.linkedin_url) {
      window.open(target.linkedin_url, "_blank", "noopener,noreferrer");
    }
    if (target.outreach_stage === "new" || target.outreach_stage === "hook_ready") {
      update(
        "contacts",
        {
          id: target.id,
          data: {
            outreach_stage: "dm_sent",
            last_seen: new Date().toISOString(),
          },
          previousData: target,
        },
        { mutationMode: "optimistic" },
      );
    }
    notify("Hook copied & LinkedIn profile opened (Stage → DM Sent)", {
      type: "info",
    });
  };

  return (
    <div className="mt-4 mb-2 rounded-xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/5 via-background to-sky-500/5 p-4 text-sm shadow-xs">
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-border/60">
        <div className="flex items-center gap-2">
          <div className="flex size-6 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
            <Sparkles className="size-3.5" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
            AI Outbound Co-Pilot
          </span>
          <LeadScoreBadge contact={target} />
          <OutreachStageSelect contact={target} />
        </div>

        <div className="flex items-center gap-1.5">
          <CopyHookButton icebreaker={activeMessage} />
          {target.linkedin_url && (
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={handleCopyAndOpenLinkedIn}
              className="cursor-pointer gap-1.5"
            >
              <Send className="size-3.5" />
              <span>Copy & DM on LinkedIn</span>
              <ExternalLink className="size-3 opacity-75" />
            </Button>
          )}
        </div>
      </div>

      {variants.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSelectedTab(-1)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
              selectedTab === -1
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            ⚡ Primary Icebreaker
          </button>
          {variants.map((v, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setSelectedTab(idx)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
                selectedTab === idx
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              <MessageSquare className="inline size-3 mr-1" />
              {v.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 rounded-lg bg-background/80 border p-3 text-foreground leading-relaxed">
        "{activeMessage}"
      </div>

      {target.recent_post_summary && (
        <div className="mt-2.5 flex items-start gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground shrink-0">
            Recent LinkedIn Signal:
          </span>
          <span>{target.recent_post_summary}</span>
        </div>
      )}
    </div>
  );
};

export const GhostEnrichButton = ({
  contact,
  variant = "outline",
  size = "sm",
  className = "",
}: {
  contact?: Contact;
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}) => {
  const record = useRecordContext<Contact>();
  const target = contact ?? record;
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  if (!target) {
    return null;
  }

  const handleGhostEnrich = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      const fullName =
        `${target.first_name ?? ""} ${target.last_name ?? ""}`.trim();
      const expectedCompany =
        target.company_name ?? target.detected_company ?? "";

      const supabase = getSupabaseClient();
      const { data, error } = await supabase.functions.invoke(
        "ghost-enricher",
        {
          body: {
            contact_id: target.id,
            full_name: fullName,
            expected_company: expectedCompany,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (data?.job_change_flagged) {
        notify(
          `Ghost Enrich complete: Job change detected (${data.detected_company || "New Company"})!`,
          { type: "warning" },
        );
      } else {
        notify("Ghost Enrich completed successfully", { type: "info" });
      }

      refresh();
    } catch (err) {
      notify(
        err instanceof Error
          ? `Ghost Enrich failed: ${err.message}`
          : "Ghost Enrich failed",
        { type: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={loading}
      onClick={handleGhostEnrich}
      className={`cursor-pointer gap-1.5 ${className}`}
    >
      {loading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Sparkles className="size-3.5" />
      )}
      <span>{loading ? "Enriching..." : "Ghost Enrich"}</span>
    </Button>
  );
};

export const BulkGhostEnrichButton = ({
  contacts,
}: {
  contacts: Contact[];
}) => {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const notify = useNotify();
  const refresh = useRefresh();

  const handleBulkEnrich = async () => {
    if (!contacts.length || running) return;
    setRunning(true);
    setProgress({ done: 0, total: contacts.length });

    const supabase = getSupabaseClient();
    let successCount = 0;
    let jobChanges = 0;

    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i];
      const fullName = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
      const expectedCompany = c.company_name ?? c.detected_company ?? "";
      try {
        const { data, error } = await supabase.functions.invoke(
          "ghost-enricher",
          {
            body: {
              contact_id: c.id,
              full_name: fullName,
              expected_company: expectedCompany,
            },
          },
        );
        if (!error && data) {
          successCount++;
          if (data.job_change_flagged) jobChanges++;
        }
      } catch {
        // continue enriching remaining contacts
      }
      setProgress({ done: i + 1, total: contacts.length });
    }

    setRunning(false);
    notify(
      `Bulk Ghost Enrich finished: ${successCount}/${contacts.length} enriched (${jobChanges} job changes flagged)`,
      { type: "info" },
    );
    refresh();
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={running || !contacts.length}
      onClick={handleBulkEnrich}
      className="cursor-pointer gap-1.5 border-indigo-500/30 bg-indigo-500/5 hover:bg-indigo-500/10"
    >
      {running ? (
        <>
          <Loader2 className="size-3.5 animate-spin text-indigo-500" />
          <span>
            Enriching ({progress.done}/{progress.total})...
          </span>
        </>
      ) : (
        <>
          <Sparkles className="size-3.5 text-indigo-500" />
          <span>Bulk Ghost Enrich ({contacts.length})</span>
        </>
      )}
    </Button>
  );
};
